import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle, Star, MapPin, CheckCircle2, CalendarClock } from "lucide-react";
import { RenewalsPanel } from "../components/home/RenewalsPanel";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { allItemsFor, hrefFor, locationHref } from "../components/nav/navConfig";
import { useNavLocations, useNavLocation, usePins } from "../components/nav/useNavPrefs";

const ROLE_LABEL = { admin: "Admin", regional: "Regional Manager", community: "Community Manager", investor: "Investor", finance: "Finance / AP" };
// The pages each role uses most, shown as big tiles.
const ROLE_TILES = {
  admin: ["/executive", "/", "/ap", "/onboarding", "/crm", "/reports", "/budget", "/investors"],
  regional: ["/", "/sales", "/crm", "/onboarding", "/members", "/budget", "/reports", "/boards"],
  community: ["/onboarding", "/members", "/bookings", "/programming", "/newsletters", "/doors", "/crm", "/boards"],
  investor: ["/", "/sales"],
  finance: ["/ap"],
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function Tile({ item, location, testid }) {
  return (
    <Link to={hrefFor(item, location)} data-testid={testid}
      className="group bg-white border border-brand-border rounded-xl p-5 hover:border-brand-ink hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
      <item.icon className="h-5 w-5 text-brand-tertiary group-hover:text-brand-ink transition-colors" />
      <p className="mt-4 text-brand-ink font-medium text-sm">{item.label}</p>
      <p className="text-xs text-brand-muted mt-1">{item.desc}</p>
    </Link>
  );
}

function Attention({ items }) {
  if (!items) return <div className="h-24 rounded-xl bg-brand-hover animate-pulse" />;
  if (!items.length) {
    return <div className="flex items-center gap-2 text-sm text-brand-muted border border-brand-border rounded-xl px-5 py-4 bg-white" data-testid="home-all-clear"><CheckCircle2 className="h-4 w-4 text-emerald-700" /> You're all caught up. Nothing needs your attention right now.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="home-needs-attention-container">
      {items.map((i) => (
        <Link key={i.key} to={i.to} data-testid={`home-attention-card-${i.key}`}
          className={`flex items-center gap-4 rounded-xl px-5 py-4 border transition-colors duration-200 ${i.urgent ? "bg-rose-50 border-rose-200 hover:border-rose-400" : "bg-amber-50/60 border-amber-200 hover:border-amber-400"}`}>
          <span className={`font-serif text-3xl font-light ${i.urgent ? "text-rose-700" : "text-brand-ink"}`}>{i.count}</span>
          <span className="flex-1 text-sm text-brand-ink">{i.label}</span>
          <span className="text-xs text-brand-muted inline-flex items-center gap-1">{i.action} <ArrowRight className="h-3 w-3" /></span>
        </Link>
      ))}
    </div>
  );
}

const Label = ({ icon: Icon, children }) => <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-3 flex items-center gap-1.5">{Icon && <Icon className="h-3 w-3" />}{children}</p>;

export default function Home() {
  const { user } = useAuth();
  const [attention, setAttention] = useState(null);
  const locations = useNavLocations(user);
  const [location, setLocation] = useNavLocation(locations);
  const [pins] = usePins(user);
  useEffect(() => { api.get("/home/attention").then(({ data }) => setAttention(data.items)).catch(() => setAttention([])); }, []);
  const items = allItemsFor(user?.role);
  const byTo = Object.fromEntries(items.map((i) => [i.to, i]));
  const tiles = (ROLE_TILES[user?.role] || []).map((t) => byTo[t]).filter(Boolean);
  const pinned = pins.map((p) => byTo[p]).filter(Boolean);

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10 space-y-10">
        <div data-testid="home-greeting">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">{ROLE_LABEL[user?.role] || "Welcome"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-brand-ink mt-2">{greeting()}, {(user?.name || "").split(" ")[0] || "there"}.</h1>
          <p className="text-sm text-brand-muted mt-2">Start with anything below, or press <kbd className="text-[10px] border border-brand-border rounded px-1.5 py-0.5">Ctrl K</kbd> to jump to any page.</p>
        </div>

        {locations.length > 1 && (
          <section data-testid="home-location-quick-pick">
            <Label icon={MapPin}>Pick a location to focus on</Label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setLocation("")} data-testid="home-loc-all"
                className={`text-sm rounded-full px-4 py-1.5 border transition-colors ${!location ? "bg-brand-ink text-brand-page border-brand-ink" : "bg-white border-brand-border text-brand-ink hover:border-brand-ink"}`}>All locations</button>
              {locations.map((l) => (
                <button key={l} onClick={() => setLocation(l)} data-testid={`home-loc-${l.replace(/\W+/g, "-")}`}
                  className={`text-sm rounded-full px-4 py-1.5 border transition-colors ${location === l ? "bg-brand-ink text-brand-page border-brand-ink" : "bg-white border-brand-border text-brand-ink hover:border-brand-ink"}`}>{l}</button>
              ))}
            </div>
            {location && <Link to={locationHref(location)} data-testid="home-open-location" className="inline-flex items-center gap-1 mt-3 text-sm text-brand-ink hover:underline">Open {location} overview <ArrowRight className="h-3.5 w-3.5" /></Link>}
          </section>
        )}

        {user?.role !== "investor" && <section><Label icon={AlertCircle}>Needs your attention</Label><Attention items={attention} /></section>}

        {["admin", "regional", "community"].includes(user?.role) && (
          <section><Label icon={CalendarClock}>Contracts ending soon</Label><RenewalsPanel location={location} /></section>
        )}

        {pinned.length > 0 && (
          <section data-testid="home-pinned">
            <Label icon={Star}>Your pinned pages</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{pinned.map((i) => <Tile key={i.to} item={i} location={location} testid={`home-pinned-${i.to.replace(/\W+/g, "") || "financials"}`} />)}</div>
          </section>
        )}

        <section data-testid="home-role-tiles">
          <Label>What you'll use most</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{tiles.map((i) => <Tile key={i.to} item={i} location={location} testid={`home-role-tile-${i.to.replace(/\W+/g, "") || "financials"}`} />)}</div>
          <p className="text-xs text-brand-tertiary mt-4">Tip: hover any page in the menu and click the star to pin it here.</p>
        </section>
      </main>
    </div>
  );
}
